import React, { useContext } from 'react';

import { compose } from 'redux';

import AJAX from 'common/AJAX';
import connect from 'common/core/connect';
import Helmet from 'common/helmets/Helmet';
import withAccessControl from 'common/routing/withAccessControl';
import SpinnerV2 from 'common/SpinnerV2';
import { hexToStatusColor } from 'common/statuses/StatusColors';
import AdminStatusSettings, {
  type SaveStatusesResponse,
  type StatusItem,
} from 'common/subdomain/admin/AdminStatusSettings';
import { IdeasConfigurationContext } from 'common/subdomain/admin/v2/containers/IdeasConfigurationContainer';
import { isNotNil } from 'common/util/isNil';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import unique from 'common/util/unique';

import type { IdeasConfiguration } from 'common/api/endpoints/idea';
import type { IdeaStatus } from 'common/api/resources/statuses';
import type { IdeasConfigurationState } from 'common/reducers/ideasConfiguration';

const updateStatus = async (status: StatusItem) => {
  const response = await AJAX.post('/api/ideas/status/update', {
    statusID: status._id,
    name: status.name,
    color: hexToStatusColor(status.color),
    type: status.type,
  });

  const { error } = parseAPIResponse<{ status: IdeaStatus }>(response, {
    isSuccessful: (response) => {
      return `status` in response && response.status && typeof response.status === 'object';
    },
    errors: {
      default: `Failed to update status "${status.name}", please try again later.`,
      'invalid name': `Failed to update status "${status.name}", the name should be between 1 and 30 characters.`,
      'invalid color': `Failed to update status "${status.name}", pick a valid color.`,
      'invalid type': `Failed to update status "${status.name}", pick a valid type.`,
    },
  });

  if (error) {
    return { error: error.message, success: false };
  }

  return { success: true, status };
};

const createStatus = async (status: StatusItem) => {
  const response = await AJAX.post('/api/ideas/status/create', {
    name: status.name,
    color: hexToStatusColor(status.color),
    type: status.type,
  });

  const DefaultError = `Failed to create status "${status.name}", please try again later.`;
  const { error, parsedResponse } = parseAPIResponse<{ status: IdeaStatus }>(response, {
    isSuccessful: (response) => `status` in response && typeof response.status === 'object',
    errors: {
      default: DefaultError,
      'invalid name': `Failed to create status "${status.name}", the name should be between 1 and 30 characters.`,
      'invalid color': `Failed to create status "${status.name}", pick a valid color.`,
      'invalid type': `Failed to create status "${status.name}", pick a valid type.`,
    },
  });

  if (error) {
    return { error: error.message, success: false };
  } else if (!parsedResponse) {
    return { error: DefaultError, success: false };
  }

  return { success: true, status: parsedResponse.status };
};

const deleteStatus = async (status: StatusItem) => {
  const response = await AJAX.post('/api/ideas/status/delete', {
    statusID: status._id,
    targetStatusID: status.targetStatus,
  });

  const { error } = parseAPIResponse(response, {
    isSuccessful: isDefaultSuccessResponse,
    errors: {
      default: `Failed to delete status "${status.name}", please refresh the page and try again.`,
    },
  });

  if (error) {
    return { error: error.message, success: false };
  }

  return { success: true, status };
};

const reorderStatuses = async (statuses: Pick<StatusItem, '_id'>[]) => {
  const response = await AJAX.post('/api/ideas/status/reorder', {
    statuses: statuses.map((status, index) => ({
      statusID: status._id,
      index,
    })),
  });

  const { error } = parseAPIResponse(response, {
    isSuccessful: isDefaultSuccessResponse,
    errors: {
      default: `Failed to reorder statuses, please refresh the page and try again.`,
    },
  });

  if (error) {
    return { error: error.message, success: false };
  }

  return { success: true };
};

type ConnectProps = {
  ideaStatuses: IdeasConfiguration['statuses'];
};
type OwnProps = Record<string, never>;

type Props = ConnectProps;

const AdminIdeaStatusSettings = ({ ideaStatuses }: Props) => {
  const { reloadConfiguration } = useContext(IdeasConfigurationContext);

  const isStatusUpdated = (status: StatusItem) => {
    // it's not updated if it's new or deleted
    if (status.new || status.delete) {
      return false;
    }

    const originalStatus = ideaStatuses.find((s) => s._id === status._id)!;
    const properties = ['name', 'type', 'color'] as const;
    return properties.some((property) => originalStatus[property] !== status[property]);
  };

  const processStatus = async (status: StatusItem) => {
    if (status.new) {
      return createStatus(status);
    } else if (status.delete) {
      return deleteStatus(status);
    } else if (isStatusUpdated(status)) {
      return updateStatus(status);
    } else {
      return { success: true, status };
    }
  };

  // TODO: We should be processing these operations as they happen, rather than batching them. But for
  // the sake of velocity, we decided to batch them to make it compatible with AdminPostStatusSettings,
  // so we can reuse the AdminStatusSettings component
  const saveStatuses = async (updatedStatuses: StatusItem[]): Promise<SaveStatusesResponse> => {
    // process each status individually
    const processedStatuses = await Promise.all(updatedStatuses.map(processStatus));

    // collect errors
    const errors = unique(
      processedStatuses.filter((status) => !status.success).map((status) => status.error),
    );

    if (errors.length > 0) {
      return { error: errors.join('\n'), success: false };
    }

    // reorder statuses if processing was successful
    const statusesToReorder = processedStatuses.map((status) => status.status).filter(isNotNil);
    const hasOrderChanged = statusesToReorder.some(
      (status, index) => ideaStatuses[index]?._id !== status._id,
    );

    if (hasOrderChanged) {
      const reorderResponse = await reorderStatuses(statusesToReorder);
      if (!reorderResponse.success) {
        return {
          error: reorderResponse.error ?? 'Something went wrong, please try again later.',
          success: false,
        };
      }
    }

    await reloadConfiguration();

    return { success: true };
  };

  if (ideaStatuses.length === 0) {
    return (
      <>
        <Helmet title="Idea Statuses | Canny" />
        <SpinnerV2 size="large" />
      </>
    );
  }

  return (
    <>
      <Helmet title="Idea Statuses | Canny" />
      <AdminStatusSettings
        entity="idea"
        showRoadmap={false}
        statuses={ideaStatuses}
        saveStatuses={saveStatuses}
      />
    </>
  );
};

export default compose(
  connect(({ ideasConfiguration }: { ideasConfiguration: IdeasConfigurationState }) => ({
    ideaStatuses: ideasConfiguration.data?.statuses ?? [],
  })),
  withAccessControl(
    testEveryPermission(RoutePermissions.adminSettings.postFields.statuses),
    '/admin/settings',
    { forwardRef: true },
  ),
)(AdminIdeaStatusSettings) as unknown as React.FC<OwnProps>;
